import { Typography, makeStyles } from '@material-ui/core'
import React from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

import Link from '@aletheia/common/components/Link'

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(6),
  },
  helpText: {
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
}))

export type PayoutChartDataPoint = {
  name: string // x-axis label
  far_future: number
  ea_community: number
  animal_welfare: number
  global_development: number
  far_future_projection: number | null
  ea_community_projection: number | null
  animal_welfare_projection: number | null
  global_development_projection: number | null
  animal_welfare_count: number
  ea_community_count: number
  far_future_count: number
  global_development_count: number
}

export const PayoutsByFund = ({ data }: { data: PayoutChartDataPoint[] }) => {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Payout Amounts by Fund
      </Typography>

      <PayoutsChart data={data} />

      <Typography className={classes.helpText} variant="body2">
        Cumulative amount (USD) regranted to effective projects by each fund.
        View full payout reports:{' '}
        <Link href="/funds/global-development#payout-reports">
          Global Health and Development Fund
        </Link>{' '}
        |{' '}
        <Link href="/funds/animal-welfare#payout-reports">
          Animal Welfare Fund
        </Link>{' '}
        |{' '}
        <Link href="/funds/far-future#payout-reports">
          Long-Term Future Fund
        </Link>{' '}
        |{' '}
        <Link href="/funds/ea-community#payout-reports">
          EA Infrastructure Fund
        </Link>{' '}
      </Typography>
    </div>
  )
}

export type showLines = {
  far_future?: boolean
  ea_community?: boolean
  animal_welfare?: boolean
  global_development?: boolean
}

const showAll = {
  far_future: true,
  ea_community: true,
  animal_welfare: true,
  global_development: true,
}

export const PayoutsChart = ({
  data,
  showLines = showAll,
}: {
  data: PayoutChartDataPoint[]
  showLines?: showLines
}) => {
  const lastYear = new Date().getFullYear() - 1

  return (
    <>
      <div
        style={{
          width: '100%',
          height: 300,
          fontFamily: '"Lato", sans-serif',
          marginBottom: '1em',
        }}
      >
        <ResponsiveContainer>
          <LineChart width={500} height={300} margin={{ right: 5 }} data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              style={{ fontSize: 14, fontWeight: 'bold' }}
              dataKey="name"
            />
            <YAxis
              style={{ fontSize: 14, fontWeight: 'bold' }}
              tickFormatter={(value: number) =>
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(value)
              }
            />
            <Tooltip
              separator="  "
              formatter={(value, name, props) => {
                if (
                  (name as string).endsWith('_projection') &&
                  props.payload.name == lastYear
                ) {
                  // exclude projection from tooltip for last year
                  return ['', '']
                }
                return [
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    notation: 'compact',
                    compactDisplay: 'short',
                  }).format(Number(value)),
                  fundLabels[name],
                ]
              }}
              itemStyle={{
                lineHeight: 0.7,
              }}
              contentStyle={{
                fontSize: 14,
                padding: '0.2em 0.4em',
                background: 'rgba(255,255,255,0.9)',
                borderRadius: 4,
              }}
            />
            <Legend
              formatter={(value: string) => fundLabels[value]}
              wrapperStyle={{ fontSize: 14 }}
            />
            {showLines.far_future && (
              <Line
                type="linear"
                dataKey="far_future"
                stroke="#88bf4d"
                strokeWidth={3}
              />
            )}
            {showLines.far_future && (
              <Line
                type="linear"
                legendType="none"
                dataKey="far_future_projection"
                stroke="#88bf4d"
                strokeWidth={3}
                strokeDasharray={'5 5'}
              />
            )}

            {showLines.ea_community && (
              <Line
                type="linear"
                dataKey="ea_community"
                stroke="#a989c5"
                strokeWidth={3}
              />
            )}
            {showLines.ea_community && (
              <Line
                type="linear"
                legendType="none"
                dataKey="ea_community_projection"
                stroke="#a989c5"
                strokeWidth={3}
                strokeDasharray={'5 5'}
              />
            )}

            {showLines.animal_welfare && (
              <Line
                type="linear"
                dataKey="animal_welfare"
                stroke="#f2a86f"
                strokeWidth={3}
              />
            )}
            {showLines.animal_welfare && (
              <Line
                type="linear"
                legendType="none"
                dataKey="animal_welfare_projection"
                stroke="#f2a86f"
                strokeWidth={3}
                strokeDasharray={'5 5'}
              />
            )}

            {showLines.global_development && (
              <Line
                type="linear"
                dataKey="global_development"
                stroke="#98d9d9"
                strokeWidth={3}
              />
            )}
            {showLines.global_development && (
              <Line
                type="linear"
                legendType="none"
                dataKey="global_development_projection"
                stroke="#98d9d9"
                strokeWidth={3}
                strokeDasharray={'5 5'}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Typography variant="body2" gutterBottom>
        Note: data for {data[data.length - 1].name} may be incomplete.
      </Typography>
    </>
  )
}

export const fundLabels: Record<string, string> = {
  far_future: 'Long-Term Future',
  ea_community: 'EA Infrastructure',
  animal_welfare: 'Animal Welfare',
  global_development: 'Global Health and Development',
  far_future_projection: 'Long-Term Future (projection)',
  ea_community_projection: 'EA Infrastructure (projection)',
  animal_welfare_projection: 'Animal Welfare (projection)',
  global_development_projection: 'Global Health and Development (projection)',
}
